import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { BundleGroupsService } from 'api/addons-v3/services';
import { SortType } from 'api-v3/models';
import { GetBundleGroups$Params } from 'api/addons-v3/fn/bundle-groups/get-bundle-groups';
import { UserRoles } from 'api/models';
import {
    BundleGroupsStore,
    BundleGroupData,
} from '@app/stores/bundleGroupsStore';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';
import { getSingleFilterParams } from '@app/shared/utils/paginated-search';

type BundleGroupSortBy = GetBundleGroups$Params['sortBy'];
type BundleGroupFilterProperty = GetBundleGroups$Params['filterProperty'];

/**
 * Maps an arbitrary sort field onto a value the bundle groups endpoint
 * accepts. The allowed fields are supplied by the caller (the view config's
 * `validSortFields`) so we don't duplicate the backend-generated union here.
 * Anything not part of that list (e.g. a sort field carried over from another
 * view) falls back to 'createdAt'.
 */
function toBundleGroupSortBy(
    sortBy: string | null | undefined,
    validSortFields: readonly string[]
): BundleGroupSortBy {
    return sortBy && validSortFields.includes(sortBy)
        ? (sortBy as BundleGroupSortBy)
        : 'createdAt';
}

@Injectable({
    providedIn: 'root',
})
export class ConsigneeBundleGroupsPaginatedService {
    private bundleGroupsStore = inject(BundleGroupsStore);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);
    private bundleGroupsService = inject(BundleGroupsService);

    async loadConsigneeBundleGroups(
        organizationSiteKey: string,
        validSortFields: readonly string[] = []
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (this.bundleGroupsStore.loading()) {
            return;
        }

        this.bundleGroupsStore.setBundleGroupsLoading(true);
        this.bundleGroupsStore.setBundleGroupsError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = this.searchStore.cancelOngoingRequests();

        try {
            if (!organizationSiteKey) {
                return;
            }

            // Check if request was cancelled before proceeding
            if (abortSignal?.aborted) {
                return;
            }

            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const columnFilters = this.searchStore.columnFilters();

            const currentSite = this.appStore.currentOrganizationSite();

            const sortDirection =
                sort.sortDirection === 'asc' ? SortType.Asc : SortType.Desc;

            // The endpoint takes one filterProperty/filterValue pair and no
            // full-text search - the search store keeps a single column filter
            // for this view and the search bar is hidden.
            const { filterProperty, filterValue } =
                getSingleFilterParams(columnFilters);
            const params: GetBundleGroups$Params = {
                organizationSiteKey,
                limit: pagination.limit,
                offset: pagination.offset,
                sort: sortDirection,
                sortBy: toBundleGroupSortBy(sort.sortBy, validSortFields),
                filterProperty: filterProperty as BundleGroupFilterProperty,
                filterValue,
            };

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            const response = await lastValueFrom(
                this.bundleGroupsService.getBundleGroups(params, context)
            );

            if (response && response.data) {
                const bundleGroups: BundleGroupData[] = response.data.map(
                    group => ({
                        ...group,
                        orgSiteKey: organizationSiteKey,
                        read_only: currentSite
                            ? currentSite.roles.includes(
                                  UserRoles.RemoteConsignee
                              ) &&
                              !currentSite.roles.includes(UserRoles.Consignee)
                            : false,
                    })
                );

                this.bundleGroupsStore.setBundleGroups(bundleGroups);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(
                    response.paginationInfo
                );
            }
        } catch (error: any) {
            // Handle cancelled requests gracefully
            if (
                error?.name === 'AbortError' ||
                error?.message === 'Subscription has been cancelled' ||
                abortSignal?.aborted
            ) {
                return;
            }

            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_BUNDLE_GROUPS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_BUNDLE_GROUPS';

            this.bundleGroupsStore.clearBundleGroups();
            this.bundleGroupsStore.setBundleGroupsError(errorMessage);

            this.searchStore.resetPaginationToEmpty();
        } finally {
            if (!abortSignal?.aborted) {
                this.bundleGroupsStore.setBundleGroupsLoading(false);
            }
        }
    }
}
